import { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { getFilters } from '../../../../../services/savedFiltersService';
import { userAppContext } from '../../../../../context/Context';
import { UserContext } from '../../../../../types';
import SharedButton from '@/global/Button/Button';
import { AdjustmentsVerticalIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import Loader from '@/components/Common/Loader';

interface SavedScore {
  id?: string;
  key?: string;
  name: string;
  sourceWeights: Array<{ name: string; score: number }>;
  savedAt: string;
  searchContext?: {
    view: string;
    tab: string;
  };
}

interface SavedScoresDropdownProps {
  tab: string;
  onApplyScore: (score: SavedScore) => void;
  onDeleteScore: (id: string) => void;
  onConfigureNew: () => void;
  onClearWeights: () => void;
  hasActiveWeights: boolean;
  refreshTrigger?: number;
  /** When true, dropdown aligns to sidebar (full width, left-aligned) */
  inline?: boolean;
  /** When true, only the panel content is rendered (no button); for sidebar use */
  contentOnly?: boolean;
}

export default function SavedScoresDropdown({
  tab,
  onApplyScore,
  onConfigureNew,
  onClearWeights,
  hasActiveWeights,
  refreshTrigger = 0,
  inline = false,
  contentOnly = false,
}: SavedScoresDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [savedScores, setSavedScores] = useState<SavedScore[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, user } = userAppContext() as unknown as UserContext;
  const isTrial = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'].includes('ROLE_TRIAL');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadSavedScores();
  }, [tab, refreshTrigger]);

  useEffect(() => {
    if (contentOnly) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, contentOnly]);

  const loadSavedScores = async () => {
    setIsLoading(true);
    try {
      const scores: SavedScore[] = [];

      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('savedScore_')) {
          try {
            const score = JSON.parse(localStorage.getItem(key) || '');
            scores.push({
              key,
              ...score,
            });
          } catch (error) {
            console.error('Error parsing saved score:', error);
          }
        }
      });

      if (isAuthenticated) {
        try {
          const response = await getFilters();
          if (response.filters && Array.isArray(response.filters)) {
            const scoreFilters = response.filters
              .filter(f => f.type === 'score')
              .map(f => ({
                id: f.id,
                name: f.name,
                sourceWeights: f.sourceWeights,
                savedAt: f.createdAt || f.savedAt,
                searchContext: f.searchContext,
              }));
            scores.push(...scoreFilters);
          }
        } catch (error) {
          console.error('Error loading saved scores from DynamoDB:', error);
        }
      }

      const filteredScores = scores.filter(score => {
        if (!score.searchContext) return true;
        return score.searchContext.tab === tab;
      });

      filteredScores.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());

      setSavedScores(filteredScores);
    } finally {
      setIsLoading(false);
    }
  };

  const panelContent = (
    <div className="min-w-0 max-h-96 overflow-auto text-center">
      {/* Active Configuration Info */}
      {hasActiveWeights && (
        <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md mb-2">
          <div className="flex items-start gap-2">
            <svg
              className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-blue-900">Score Configuration Active</p>
              <p className="text-xs text-blue-700 mt-1">
                Use &quot;Clear Active Weights&quot; below to remove it.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Configure New / Clear Weights */}
      <div className="space-y-2 mb-2">
        <SharedButton
          variant="secondaryOutline"
          onClick={() => {
            if (!contentOnly) setIsOpen(false);
            onConfigureNew();
          }}
        >
          + Configure New Score
        </SharedButton>
        {hasActiveWeights && (
          <SharedButton
            variant="terciary"
            onClick={() => {
              if (!contentOnly) setIsOpen(false);
              onClearWeights();
            }}
          >
            Clear Active Weights
          </SharedButton>
        )}
      </div>

      {/* Saved Scores List */}
      <div className="py-1">
        {isLoading ? (
          <div className="px-3 py-2 ">
            <Loader isInnerSpinner />
          </div>
        ) : savedScores.length === 0 && !isTrial ? (
          <div className="px-2 py-4 text-center">
            <p className="text-sm text-gray-500">No saved scores yet</p>
            <p className="text-xs text-gray-400 mt-1">Configure and save a score to see it here</p>
          </div>
        ) : savedScores.length > 0 ? (
          savedScores.map((score, index) => (
            <div
              key={score.id || score.key || index}
              className="px-2 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 rounded"
            >
              <div className="flex flex-col items-start justify-between gap-2 rounded bg-gray-50 p-4">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate">{score.name}</h4>
                  <p className="text-xs text-gray-500 mt-0.5 text-left">
                    {new Date(score.savedAt).toLocaleDateString()}{' '}
                    {new Date(score.savedAt).toLocaleTimeString()}
                  </p>
                  {score.sourceWeights && score.sourceWeights.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {score.sourceWeights.slice(0, 3).map((sw, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-gray-600 truncate">{sw.name.replace('_', ' ')}</span>
                          <span className="font-medium text-gray-900 flex-shrink-0 ml-1">
                            {sw.score.toFixed(2)}
                          </span>
                        </div>
                      ))}
                      {score.sourceWeights.length > 3 && (
                        <p className="text-xs text-gray-400">+{score.sourceWeights.length - 3} more</p>
                      )}
                    </div>
                  )}
                </div>
                <SharedButton
                  variant="secondaryText"
                  iconRight={<ArrowRightIcon className="w-4 h-4 ml-1" />}
                  size="xs"
                  className="flex-shrink-0 mb-0 text-left ml-0"
                  onClick={() => {
                    if (!contentOnly) setIsOpen(false);
                    onApplyScore(score);
                  }}
                >
                  Apply
                </SharedButton>
              </div>
            </div>
          ))
        ) : null}
      </div>
    </div>
  );

  if (contentOnly) {
    return (
      <div ref={dropdownRef} className="w-full min-w-0">
        {panelContent}
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className={inline ? 'relative w-full' : 'relative inline-block'}>
      <SharedButton
        onClick={() => setIsOpen(!isOpen)}
        variant="secondary"
        size="md"
        className={inline ? 'w-full justify-between' : ''}
      >
        <AdjustmentsVerticalIcon className="w-5 h-5 mr-1" />
        My Score
        <ChevronDownIcon
          className="w-4 h-4 ml-1"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </SharedButton>
      {isOpen && (
        <div
          className={
            inline
              ? 'absolute left-0 right-0 mt-2 w-full min-w-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-auto'
              : 'absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-auto'
          }
        >
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-900">Saved Score Configurations</h3>
          </div>
          <div className="p-4">{panelContent}</div>
        </div>
      )}
    </div>
  );
}
